"use server";

import { prisma } from "@/lib/prisma";
import { sendBookingConfirmation } from "@/lib/email";
import { revalidatePath } from "next/cache";

export type BookingResult =
  | { success: true }
  | { success: false; error: "not_enough_seats" | "invalid_input" | "unknown" };

const dateLocales: Record<string, string> = {
  no: "nb-NO",
  en: "en-GB",
  de: "de-DE",
  fr: "fr-FR",
  zh: "zh-CN",
};

export async function createBooking(input: {
  tourSlug: string;
  locale: string;
  departureId: string;
  packageId: string;
  customerName: string;
  email: string;
  phone?: string;
  seats: number;
  allergies?: string;
}): Promise<BookingResult> {
  if (!input.customerName.trim() || !input.email.trim() || input.seats < 1) {
    return { success: false, error: "invalid_input" };
  }

  try {
    await prisma.$transaction(async (tx) => {
      const rows = await tx.$queryRaw<{ capacity: number }[]>`
        SELECT capacity FROM "Departure" WHERE id = ${input.departureId} FOR UPDATE
      `;
      const departure = rows[0];
      if (!departure) {
        throw new Error("departure_not_found");
      }

      const bookedResult = await tx.booking.aggregate({
        where: { departureId: input.departureId, status: "CONFIRMED" },
        _sum: { seats: true },
      });
      const alreadyBooked = bookedResult._sum.seats ?? 0;

      if (alreadyBooked + input.seats > departure.capacity) {
        throw new Error("not_enough_seats");
      }

      await tx.booking.create({
        data: {
          departureId: input.departureId,
          packageId: input.packageId,
          customerName: input.customerName.trim(),
          email: input.email.trim(),
          phone: input.phone?.trim() || null,
          seats: input.seats,
          allergies: input.allergies?.trim() || null,
          status: "CONFIRMED",
          source: "WEBSITE",
        },
      });
    });

    revalidatePath("/", "layout");

    // Sender bekreftelses-e-post. Feiler dette, feiler ikke selve
    // bookingen - vi logger bare feilen, siden plassen allerede er sikret.
    try {
      const [departure, pkg] = await Promise.all([
        prisma.departure.findUnique({
          where: { id: input.departureId },
          include: { tour: { include: { translations: true } } },
        }),
        prisma.package.findUnique({
          where: { id: input.packageId },
          include: { translations: true },
        }),
      ]);

      if (departure && pkg) {
        const tourTitle =
          departure.tour.translations.find((t) => t.locale === input.locale)
            ?.title ??
          departure.tour.translations.find((t) => t.locale === "no")?.title ??
          "";
        const packageName =
          pkg.translations.find((t) => t.locale === input.locale)?.name ??
          pkg.translations.find((t) => t.locale === "no")?.name ??
          "";
        const departureLabel = new Intl.DateTimeFormat(
          dateLocales[input.locale] ?? "en-GB",
          {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          }
        ).format(departure.departureAt);

        await sendBookingConfirmation({
          to: input.email.trim(),
          locale: input.locale,
          customerName: input.customerName.trim(),
          tourTitle,
          packageName,
          departureLabel,
          seats: input.seats,
          priceNok: Number(pkg.priceNok),
        });
      }
    } catch (emailError) {
      console.error("Klarte ikke sende bekreftelses-e-post:", emailError);
    }

    return { success: true };
  } catch (e) {
    if (e instanceof Error && e.message === "not_enough_seats") {
      return { success: false, error: "not_enough_seats" };
    }
    console.error(e);
    return { success: false, error: "unknown" };
  }
}
